import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { api, API_BASE } from "../lib/api";
import { Plus, Send, Paperclip, Trash2, Sparkles, ChevronRight, FileText, X, Image as ImageIcon, Loader2, Check, CircleAlert } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { closeReservedWorkspace, openPraticaWorkspace, reservePraticaWorkspace } from "../lib/praticaWorkspace";

export default function Chat() {
    const { user, refreshUser, setUser } = useAuth();
    const { t, lang, dir } = useI18n();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [conversations, setConversations] = useState([]);
    const [activeConvId, setActiveConvId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [images, setImages] = useState([]); // [{name, data}]
    const [streaming, setStreaming] = useState(false);
    const [streamingText, setStreamingText] = useState("");
    const [streamingStatus, setStreamingStatus] = useState("");
    const [conversationJobs, setConversationJobs] = useState([]);
    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const isAdmin = user?.role === "super_admin";

    const loadConversations = useCallback(async () => {
        try {
            const r = await api.get("/chat/conversations");
            setConversations(r.data);
        } catch {}
    }, []);

    const loadConversation = useCallback(async (convId) => {
        if (!convId) { setMessages([]); setConversationJobs([]); return; }
        try {
            const [r, jobs] = await Promise.all([
                api.get(`/chat/conversations/${convId}`),
                api.get(`/chat/conversations/${convId}/jobs`),
            ]);
            setMessages(r.data.messages || []);
            setConversationJobs(jobs.data || []);
        } catch {}
    }, []);

    useEffect(() => { loadConversations(); }, [loadConversations]);
    useEffect(() => {
        const requestedConversation = searchParams.get("conversation");
        if (requestedConversation) setActiveConvId(requestedConversation);
    }, [searchParams]);
    useEffect(() => { loadConversation(activeConvId); }, [activeConvId, loadConversation]);
    useEffect(() => {
        if (!activeConvId || !conversationJobs.some(job => ["queued", "processing"].includes(job.status))) return undefined;
        const timer = window.setInterval(async () => {
            try {
                const r = await api.get(`/chat/conversations/${activeConvId}/jobs`);
                const jobs = r.data || [];
                const hasActive = jobs.some(job => ["queued", "processing"].includes(job.status));
                setConversationJobs(jobs);
                if (!hasActive) {
                    await loadConversation(activeConvId);
                    await loadConversations();
                    await refreshUser?.();
                }
            } catch {}
        }, 1500);
        return () => window.clearInterval(timer);
    }, [activeConvId, conversationJobs, loadConversation, loadConversations, refreshUser]);
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, streamingText]);

    const newConversation = () => {
        setActiveConvId(null);
        setMessages([]);
        setStreamingText("");
        setStreamingStatus("");
    };

    const deleteConv = async (id) => {
        if (!window.confirm(t.chat.confirmDelete)) return;
        try { await api.delete(`/chat/conversations/${id}`); } catch {}
        if (activeConvId === id) newConversation();
        loadConversations();
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        const validFiles = files.filter(f => f.type.startsWith("image/") || f.type === "application/pdf");
        validFiles.forEach((f) => {
            const reader = new FileReader();
            reader.onload = () => {
                setImages((prev) => [...prev, { name: f.name, data: reader.result, mime: f.type }]);
            };
            reader.readAsDataURL(f);
        });
        e.target.value = "";
    };

    const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

    const send = async (overrideText) => {
        const payload = overrideText || text.trim();
        if (!payload && images.length === 0) return;
        if (streaming) return;

        // Optimistic user message
        const tempUser = {
            id: `local-${Date.now()}`,
            role: "user",
            text: payload,
            images_count: images.length,
            created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, tempUser]);
        setText("");
        const currentImages = images;
        setImages([]);

        // Every conversational command runs as a durable server task. The
        // mounted page only observes it and never owns/cancels its lifecycle.
        if (currentImages.length > 0 || payload) {
            try {
                const r = await api.post("/chat/jobs", {
                    conversation_id: activeConvId,
                    text: payload,
                    images_base64: currentImages.map(i => i.data),
                    lang,
                    module_code: "FISCOX_RECEPTION",
                });
                setActiveConvId(r.data.conversation_id);
                setConversationJobs((previous) => [{
                    id: r.data.job_id,
                    conversation_id: r.data.conversation_id,
                    status: r.data.status,
                    phase: "queued",
                    message: currentImages.length > 0
                        ? "Documento accodato: puoi cambiare chat o pratica"
                        : "Attività accodata: puoi continuare a lavorare altrove",
                }, ...previous.filter(job => job.id !== r.data.job_id)]);
                setStreaming(false);
                setStreamingText("");
                setStreamingStatus("");
                loadConversations();
            } catch (e) {
                setMessages((prev) => [...prev, {
                    id: `local-err-${Date.now()}`,
                    role: "assistant",
                    text: `Errore: ${e?.response?.data?.detail || e?.message || "Impossibile accodare il documento"}`,
                    created_at: new Date().toISOString(),
                }]);
                setStreaming(false);
                setStreamingStatus("");
            }
            return;
        }
        setStreaming(true);
        setStreamingText("");
        setStreamingStatus(images.length > 0 ? "Preparazione del documento..." : "FiscoAI sta scrivendo...");

        try {
            const token = localStorage.getItem("fiscoai_token");
            const resp = await fetch(`${API_BASE}/chat/send`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    conversation_id: activeConvId,
                    text: payload,
                    images_base64: currentImages.map(i => i.data),
                    lang,
                }),
            });
            if (!resp.ok) {
                const errBody = await resp.json().catch(() => ({}));
                throw new Error(errBody?.detail || `HTTP ${resp.status}`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let acc = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n\n");
                buffer = lines.pop() || "";
                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    try {
                        const ev = JSON.parse(line.slice(6));
                        if (ev.type === "delta") {
                            acc += ev.content;
                            setStreamingText(acc);
                            setStreamingStatus("");
                        } else if (ev.type === "progress") {
                            setStreamingStatus(ev.message || "Analisi del documento in corso...");
                        } else if (ev.type === "done") {
                            setActiveConvId(ev.conversation_id);
                            if (typeof ev.credits_balance === "number") {
                                setUser((u) => u ? { ...u, credits_balance: ev.credits_balance } : u);
                                localStorage.setItem("fiscoai_user", JSON.stringify({ ...(user || {}), credits_balance: ev.credits_balance }));
                            }
                        } else if (ev.type === "error") {
                            acc += `\n\n[Errore: ${ev.message}]`;
                            setStreamingText(acc);
                        }
                    } catch (e) { /* ignore parse */ }
                }
            }
            // Push final assistant message
            setMessages((prev) => [...prev, {
                id: `local-asst-${Date.now()}`,
                role: "assistant",
                text: acc,
                created_at: new Date().toISOString(),
            }]);
            setStreamingText("");
            // Reload conversations list (for new title)
            loadConversations();
        } catch (e) {
            const msg = e?.message || "Errore";
            setMessages((prev) => [...prev, {
                id: `local-err-${Date.now()}`,
                role: "assistant",
                text: `Errore: ${msg}`,
                created_at: new Date().toISOString(),
            }]);
        } finally {
            setStreaming(false);
            setStreamingText("");
            setStreamingStatus("");
        }
    };

    const sendSuggested = (body) => {
        setText(body);
        setTimeout(() => send(body), 50);
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <div className="flex h-screen">
            {/* Conversations panel */}
            <div className="w-64 shrink-0 border-e border-border bg-background-alt/30 flex flex-col">
                <div className="p-3 border-b border-border">
                    <button
                        data-testid="new-chat-button"
                        onClick={newConversation}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md border border-border bg-card hover:bg-accent text-sm font-medium transition-colors"
                    >
                        <Plus size={16} /> {t.chat.newChat}
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                    {conversations.length === 0 && (
                        <div className="text-xs text-muted-foreground px-3 py-4 text-center">{t.chat.emptyConversations}</div>
                    )}
                    {conversations.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`conv-item-${c.id}`}
                            className={`group flex items-center justify-between px-3 py-2 rounded-md text-sm cursor-pointer transition-colors ${activeConvId === c.id ? "bg-accent text-foreground" : "hover:bg-accent/60 text-foreground/80"}`}
                            onClick={() => setActiveConvId(c.id)}
                        >
                            <span className="truncate flex-1 min-w-0">{c.title || "..."}</span>
                            <button
                                data-testid={`conv-delete-${c.id}`}
                                onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
                            ><Trash2 size={12} /></button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main chat */}
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex-1 overflow-y-auto" ref={scrollRef}>
                    <div className="max-w-3xl mx-auto px-6 py-12">
                        {messages.length === 0 && !streamingText && (
                            <EmptyState onPick={sendSuggested} t={t} />
                        )}
                        {messages.map((m, i) => (
                            <Message key={m.id || i} msg={m} t={t} />
                        ))}
                        {streamingText && (
                            <Message msg={{ role: "assistant", text: streamingText }} streaming t={t} />
                        )}
                        {streaming && !streamingText && (
                            <div data-testid="chat-streaming-indicator" className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
                                <Loader2 size={14} className="animate-spin" />
                                <span>{streamingStatus || "FiscoAI sta scrivendo..."}</span>
                            </div>
                        )}
                        {conversationJobs
                            .filter(job => ["queued", "processing", "failed"].includes(job.status))
                            .map(job => <ChatTaskStatus key={job.id} job={job} />)}
                    </div>
                </div>

                {/* Composer */}
                <div className="border-t border-border bg-background">
                    <div className="max-w-3xl mx-auto px-6 py-4">
                        {/* Attachments preview */}
                        {images.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {images.map((img, i) => (
                                    <div key={i} className="relative group" data-testid={`attachment-tile-${i}`}>
                                        <div className="w-16 h-16 rounded-md border border-border overflow-hidden bg-accent flex items-center justify-center">
                                            {img.mime === "application/pdf" ? (
                                                <div data-testid={`pdf-preview-tile-${i}`} className="flex flex-col items-center justify-center w-full h-full text-foreground/70">
                                                    <FileText size={20} />
                                                    <span className="text-[9px] uppercase tracking-wider font-bold mt-0.5">PDF</span>
                                                </div>
                                            ) : (
                                                <img src={img.data} alt={img.name} className="w-full h-full object-cover" />
                                            )}
                                        </div>
                                        <button
                                            data-testid={`remove-image-${i}`}
                                            onClick={() => removeImage(i)}
                                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center"
                                        ><X size={10} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex items-end gap-2 rounded-xl border border-border bg-card focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition px-3 py-2">
                            <button
                                data-testid="chat-attach-button"
                                onClick={() => fileInputRef.current?.click()}
                                className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                                title={t.chat.attach}
                            >
                                <Paperclip size={18} />
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,application/pdf"
                                multiple
                                onChange={handleFileSelect}
                                className="hidden"
                                data-testid="chat-file-input"
                            />
                            <textarea
                                data-testid="chat-input"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={onKey}
                                placeholder={t.chat.placeholder}
                                rows={1}
                                className="flex-1 resize-none bg-transparent outline-none text-sm py-2 max-h-32 overflow-y-auto"
                                disabled={streaming}
                            />
                            <button
                                data-testid="chat-send-button"
                                onClick={() => send()}
                                disabled={streaming || (!text.trim() && images.length === 0)}
                                className="p-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors flex-shrink-0"
                            >
                                {streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} className="rtl-flip" />}
                            </button>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                            <div className="text-[11px] text-muted-foreground">
                                {!isAdmin && user && <>Msg AI 0.10 cr · OCR pagina 0.50 cr · invio P.IVA AdE secondo tariffario</>}
                                {isAdmin && <>Modalità Super Admin — nessun consumo crediti</>}
                            </div>
                            {!isAdmin && user && (
                                <div className="text-[11px] text-muted-foreground">
                                    <Sparkles size={10} className="inline me-1 text-primary" />
                                    {Number(user.credits_balance || 0).toFixed(2)} crediti disponibili
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChatTaskStatus({ job }) {
    const active = ["queued", "processing"].includes(job.status);
    const failed = job.status === "failed";
    const total = Number(job.total_pages || 0);
    const processed = Number(job.processed_pages || 0);
    const progress = total > 0 ? Math.min(100, processed / total * 100) : 0;
    return (
        <div
            data-testid={`chat-task-${job.id}`}
            className={`my-4 rounded-lg border px-4 py-3 text-sm ${failed ? "border-destructive/30 bg-destructive/5" : "border-primary/20 bg-primary/5"}`}
        >
            <div className="flex items-center gap-2 font-medium">
                {active ? <Loader2 size={15} className="animate-spin" /> : <CircleAlert size={15} className="text-destructive" />}
                {failed ? "Elaborazione non riuscita" : "Elaborazione in corso"}
            </div>
            <div className="mt-1 text-muted-foreground">{job.message || (failed ? "Riprova l'attività dalla chat" : "Analisi del documento in corso")}</div>
            {active && total > 0 && (
                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                </div>
            )}
            {active && <div className="mt-2 text-xs text-muted-foreground">Puoi navigare altrove: questa attività continuerà sul server e resterà visibile in questa chat.</div>}
        </div>
    );
}

function EmptyState({ onPick, t }) {
    return (
        <div className="py-16 animate-fade-in">
            <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase mb-6">
                    <Sparkles size={12} /> FiscoAI · Assistente AI
                </div>
                <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-3">{t.chat.welcomeTitle}</h1>
                <p className="text-base text-muted-foreground">{t.chat.welcomeSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {t.chat.suggested.map((s, i) => (
                    <button
                        key={i}
                        data-testid={`suggested-prompt-${i}`}
                        onClick={() => onPick(s.body)}
                        className="text-start group p-4 rounded-lg border border-border bg-card hover:bg-accent hover:border-primary/30 transition-all"
                    >
                        <div className="font-medium text-sm mb-1 text-foreground">{s.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{s.body}</div>
                        <div className="mt-2 flex items-center gap-1 text-[11px] text-primary opacity-0 group-hover:opacity-100 transition-opacity font-semibold uppercase tracking-wider">
                            Prova <ChevronRight size={12} className="rtl-flip" />
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

function Message({ msg, streaming, t }) {
    const navigate = useNavigate();
    const isUser = msg.role === "user";

    // Parse fiscoai-form block if present
    const formMatch = !isUser && msg.text ? msg.text.match(/```fiscoai-form\s*([\s\S]*?)```/) : null;
    let parsedForm = null;
    if (formMatch) {
        try { parsedForm = JSON.parse(formMatch[1]); } catch {}
    }
    const textWithoutForm = formMatch ? msg.text.replace(formMatch[0], "").trim() : (msg.text || "");

    const handleCreatePratica = async (moduleCode, tipoDich) => {
        if (!parsedForm) return;
        const workspaceTab = reservePraticaWorkspace(`Nuova pratica — ${moduleCode}`);
        try {
            const isPivaModule = moduleCode === "AA9_12" || moduleCode === "AA7_10";
            const fd = isPivaModule
                ? { ...(parsedForm.form_data || {}), tipo_dichiarazione: tipoDich }
                : { ...(parsedForm.form_data || {}) };
            const r = await api.post("/pratiche", {
                module_code: moduleCode,
                form_data: fd,
                title: `${moduleCode} — generata da chat`,
            });
            openPraticaWorkspace(r.data.id, workspaceTab, navigate);
        } catch (e) {
            closeReservedWorkspace(workspaceTab);
            alert("Errore creazione pratica: " + (e?.response?.data?.detail || e.message));
        }
    };

    return (
        <div data-testid={`chat-message-${msg.role}`} className="py-4 animate-fade-in">
            <div className="flex items-center gap-2 mb-2">
                {isUser ? (
                    <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-xs font-semibold">
                        {t.common.you[0]}
                    </div>
                ) : (
                    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-display font-bold">F</div>
                )}
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {isUser ? t.common.you : t.common.ai}
                </span>
            </div>
            <div className={`chat-prose ${isUser ? "bg-user-bubble rounded-lg px-4 py-3" : "px-1"} text-sm leading-relaxed text-foreground`}>
                {textWithoutForm}
                {streaming && <span className="ms-1 inline-block w-2 h-4 bg-primary/60 align-middle animate-pulse" />}
            </div>

            {!isUser && parsedForm && (
                <FormCard data={parsedForm} onConfirm={handleCreatePratica} />
            )}
        </div>
    );
}

function FormCard({ data, onConfirm }) {
    const form = data?.form_data || {};
    const qb = form.quadro_b || {};
    const qc = form.quadro_c || {};
    const qd = form.quadro_d || {};
    const proposed = data?.module_code || "AA9_12";
    const isAnagrafica = proposed === "AA9_12" || proposed === "AA7_10";
    const isDichiarativoAde = ["REDDITI_PF", "REDDITI_SC", "REDDITI_SP", "IVA_ANNUALE", "IRAP", "CU"].includes(proposed);
    const populatedQuadri = Object.entries(form.quadri || {})
        .filter(([, value]) => value != null && value !== "" && (!(Array.isArray(value)) || value.length > 0) && (typeof value !== "object" || Array.isArray(value) || Object.keys(value).length > 0))
        .map(([code]) => code);
    const importedOfficialFields = form?._source_import?.official_fields || [];
    const [moduleCode, setModuleCode] = useState(proposed);
    const [tipoDich, setTipoDich] = useState(String(form.tipo_dichiarazione || "1"));
    const isPnf = moduleCode === "AA7_10";

    return (
        <div data-testid="ai-form-card" className="mt-3 rounded-lg border border-primary/30 bg-card overflow-hidden">
            <div className="px-4 py-2.5 bg-primary/5 border-b border-primary/20 flex items-center gap-2">
                <FileText size={14} className="text-primary" />
                <div className="text-sm font-semibold">
                    {isAnagrafica ? `Pratica ${moduleCode.replace("_", "/")}` : `Pratica ${proposed}`}
                </div>
                {isAnagrafica && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold uppercase tracking-wider">
                        Proposta AI: {proposed === "AA9_12" ? "Persona fisica" : "Società/Ente"}
                    </span>
                )}
            </div>
            {isAnagrafica && (
                <div className="px-4 py-3 border-b border-border grid sm:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Modello</span>
                        <select
                            data-testid="chat-form-module-select"
                            value={moduleCode}
                            onChange={(e) => setModuleCode(e.target.value)}
                            className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                        >
                            <option value="AA9_12">AA9/12 — Persona fisica (ditta individuale / professionista)</option>
                            <option value="AA7_10">AA7/10 — Società o ente (soggetto diverso da PF)</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Tipo pratica</span>
                        <select
                            data-testid="chat-form-tipo-select"
                            value={tipoDich}
                            onChange={(e) => setTipoDich(e.target.value)}
                            className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm focus:ring-2 focus:ring-primary/20 outline-none"
                        >
                            <option value="1">Apertura / Inizio attività</option>
                            <option value="2">Variazione dati</option>
                            <option value="3">Cessazione attività</option>
                        </select>
                    </label>
                    {moduleCode !== proposed && (
                        <div data-testid="chat-form-module-warning" className="sm:col-span-2 text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                            Hai cambiato modello rispetto alla proposta dell'AI: verifica i dati nella pratica prima di generare il file.
                        </div>
                    )}
                </div>
            )}
            <div className="p-4 grid grid-cols-2 gap-3 text-xs">
                {isDichiarativoAde ? (
                    <>
                        <Info label="Modulo" value={proposed} />
                        <Info label="Anno modello" value={form.meta?.anno_modello} />
                        <Info label="Periodo imposta" value={form.meta?.periodo_imposta} />
                        <Info label="Quadri compilati" value={populatedQuadri.join(", ")} />
                        <Info label="Pagine PDF analizzate" value={form?._source_import?.total_pages} />
                        <Info label="Campi ufficiali conservati" value={importedOfficialFields.length ? `${importedOfficialFields.length}` : ""} />
                        <Info label="Documenti importati" value={(form.documenti?.fatture || []).length ? `${(form.documenti?.fatture || []).length}` : ""} />
                        <Info label="Stato importazione" value={form?._source_import?.extraction_complete ? "Tutte le pagine analizzate — revisione richiesta" : "Estrazione parziale — revisione richiesta"} />
                    </>
                ) : isPnf ? (
                    <>
                        <Info label="Denominazione" value={qb.denominazione} />
                        <Info label="Natura giuridica" value={qb.natura_giuridica} />
                        <Info label="Codice fiscale" value={qb.codice_fiscale} />
                        <Info label="Sede legale" value={`${qb.indirizzo_sede || ""} ${qb.civico_sede || ""}, ${qb.comune_sede || ""} ${qb.provincia_sede || ""}`.trim()} />
                        <Info label="Rappresentante" value={`${qc.cognome || ""} ${qc.nome || ""}`.trim() || qc.codice_fiscale} />
                        {qd.codice_attivita_ateco && <Info label="ATECO" value={qd.codice_attivita_ateco} />}
                    </>
                ) : (
                    <>
                        <Info label="Codice fiscale" value={qb.codice_fiscale} />
                        <Info label="Cognome / Nome" value={`${qb.cognome || ""} ${qb.nome || ""}`.trim()} />
                        <Info label="Data nascita" value={qb.data_nascita} />
                        <Info label="Comune nascita" value={qb.comune_nascita} />
                        <Info label="Residenza" value={`${qb.indirizzo_residenza || ""}, ${qb.comune_residenza || ""} ${qb.provincia_residenza || ""}`} />
                        {qc.codice_attivita_ateco && <Info label="ATECO" value={qc.codice_attivita_ateco} />}
                        {qc.data_inizio_attivita && <Info label="Inizio attività" value={qc.data_inizio_attivita} />}
                        {qc.regime_fiscale && <Info label="Regime" value={qc.regime_fiscale} />}
                    </>
                )}
                {form.partita_iva && <Info label="Partita IVA" value={form.partita_iva} />}
            </div>
            <div className="px-4 py-3 bg-background-alt border-t border-border flex justify-end gap-2">
                <button
                    data-testid="create-pratica-from-chat"
                    onClick={() => onConfirm(isAnagrafica ? moduleCode : proposed, tipoDich)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
                >
                    <Check size={14} /> Crea pratica e rivedi
                </button>
            </div>
        </div>
    );
}

function Info({ label, value }) {
    if (!value) return null;
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">{label}</div>
            <div className="text-xs font-medium text-foreground">{value}</div>
        </div>
    );
}
